import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Twitter as TwitterIcon,
} from '@mui/icons-material';
import AppVerifiedUsers from '../components/app/AppVerifiedUsers';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import backendService from '../components/backendservices/BackendService';
import Loader from '../components/Loader';
import { TwitterHandle, TwitterStats } from '../types/Twitter';

const TwitterDashboard = () => {
  const [stats, setStats] = useState<TwitterStats | null>(null);
  const [handles, setHandles] = useState<TwitterHandle[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingHandle, setEditingHandle] = useState<TwitterHandle | null>(null);
  const [formData, setFormData] = useState({
    username: '',
    priority: 1,
    isActive: true,
  });
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  // Fetch Twitter stats
  const fetchStats = async () => {
    try {
      const response = await backendService.Get(
        `${process.env.REACT_APP_BASE_URL_Twitter}/twitter-handles/stats`
      );
      if (response.status === 200) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching Twitter stats:', error);
      setNotification({
        open: true,
        message: 'Failed to fetch Twitter statistics',
        severity: 'error',
      });
    }
  };

  // Fetch active Twitter handles
  const fetchHandles = async () => {
    try {
      const response = await backendService.Get(
        `${process.env.REACT_APP_BASE_URL_Twitter}/twitter-handles`
      );
      if (response.status === 200) {
        setHandles(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching Twitter handles:', error);
      setNotification({
        open: true,
        message: 'Failed to fetch Twitter handles',
        severity: 'error',
      });
    }
  };

  // Create or update Twitter handle
  const handleSubmit = async () => {
    try {
      if (editingHandle) {
        // Update existing handle
        const response = await backendService.Put(
          `${process.env.REACT_APP_BASE_URL_Twitter}/twitter-handles/${editingHandle._id}`,
          formData
        );
        if (response.status === 200) {
          setNotification({
            open: true,
            message: 'Twitter handle updated successfully',
            severity: 'success',
          });
        }
      } else {
        // Create new handle
        const response = await backendService.Post(
          `${process.env.REACT_APP_BASE_URL_Twitter}/twitter-handles/`,
          formData
        );
        if (response.status === 200) {
          setNotification({
            open: true,
            message: 'Twitter handle created successfully',
            severity: 'success',
          });
        }
      }

      setOpenDialog(false);
      setEditingHandle(null);
      setFormData({ username: '', priority: 1, isActive: true });
      fetchHandles();
      fetchStats();
    } catch (error) {
      console.error('Error saving Twitter handle:', error);
      setNotification({
        open: true,
        message: 'Failed to save Twitter handle',
        severity: 'error',
      });
    }
  };

  // Delete Twitter handle
  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this Twitter handle?')) {
      try {
        const response = await backendService.Delete(
          `${process.env.REACT_APP_BASE_URL_Twitter}/twitter-handles/${id}`
        );
        if (response.status === 200) {
          setNotification({
            open: true,
            message: 'Twitter handle deleted successfully',
            severity: 'success',
          });
          fetchHandles();
          fetchStats();
        }
      } catch (error) {
        console.error('Error deleting Twitter handle:', error);
        setNotification({
          open: true,
          message: 'Failed to delete Twitter handle',
          severity: 'error',
        });
      }
    }
  };

  // Edit Twitter handle
  const handleEdit = (handle: TwitterHandle) => {
    setEditingHandle(handle);
    setFormData({
      username: handle.username,
      priority: handle.priority,
      isActive: handle.isActive,
    });
    setOpenDialog(true);
  };

  // Open dialog for new handle
  const handleAdd = () => {
    setEditingHandle(null);
    setFormData({ username: '', priority: 1, isActive: true });
    setOpenDialog(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingHandle(null);
    setFormData({ username: '', priority: 1, isActive: true });
  };

  // Format date
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return (
      new Date(dateString).toLocaleDateString() + ' ' + new Date(dateString).toLocaleTimeString()
    );
  };

  useEffect(() => {
    const initializeData = async () => {
      setLoading(true);
      await Promise.all([fetchStats(), fetchHandles()]);
      setLoading(false);
    };
    initializeData();
  }, []);

  if (loading) {
    return (
      <Container maxWidth='xl' sx={{ p: { xs: 2, md: 3 } }}>
        <Loader />
      </Container>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        minHeight: '100vh',
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 5 }}>
        <Typography
          variant='h3'
          sx={{
            fontWeight: 900,
            color: '#6871d1',
            mb: 1,
            fontFamily: 'Lato, sans-serif',
            letterSpacing: 1,
          }}
        >
          Twitter Management 🐦
        </Typography>
        <Typography
          variant='body1'
          sx={{
            color: '#aaa',
            fontSize: '1.1rem',
            fontWeight: 500,
          }}
        >
          Monitor and manage your Twitter handles and engagement metrics
        </Typography>
      </Box>

      {/* Stats Cards */}
      {stats && (
        <Box sx={{ mb: 4 }}>
          <Typography
            variant='h4'
            sx={{
              mb: 3,
              fontWeight: 800,
              color: '#fff',
              textAlign: 'left',
              letterSpacing: 1,
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -8,
                left: 0,
                width: 60,
                height: 3,
                background: 'linear-gradient(135deg, #1DA1F2 0%, #14171A 100%)',
                borderRadius: 2,
              },
            }}
          >
            Twitter Analytics
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <AppVerifiedUsers name='Total Handles' count={stats.totalHandles} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <AppVerifiedUsers name='Active Handles' count={stats.activeHandles} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <AppVerifiedUsers name='Tweets Processed' count={stats.totalTweetsProcessed} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <AppVerifiedUsers name='Total Replies' count={stats.totalReplies} />
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Twitter Handles Table */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography
            variant='h4'
            sx={{
              fontWeight: 800,
              color: '#fff',
              textAlign: 'left',
              letterSpacing: 1,
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -8,
                left: 0,
                width: 60,
                height: 3,
                background: 'linear-gradient(135deg, #1DA1F2 0%, #14171A 100%)',
                borderRadius: 2,
              },
            }}
          >
            Twitter Handles
          </Typography>
          <Button
            variant='contained'
            startIcon={<AddIcon />}
            onClick={handleAdd}
            sx={{
              backgroundColor: '#6871d1',
              color: '#fff',
              py: 1.5,
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(104, 113, 209, 0.4)',
              fontWeight: 500,
              transition: 'all 0.3s ease',
              px: 3,
              fontSize: '1rem',
              '&:hover': {
                backgroundColor: '#5a63bb',
                boxShadow: '0 6px 16px rgba(104, 113, 209, 0.6)',
              },
              '&:disabled': {
                backgroundColor: '#aeb3e9',
                color: '#fff',
                opacity: 0.7,
              },
            }}
          >
            Add Handle
          </Button>
        </Box>

        <TableContainer
          component={Paper}
          sx={{
            background: 'linear-gradient(135deg, #2d2e48 0%, #1e1e2f 100%)',
            border: '1.5px solid #2b2c3e',
            borderRadius: 3,
            boxShadow: '0 4px 24px 0 rgba(30, 30, 47, 0.15)',
            overflow: 'hidden',
          }}
        >
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  background: 'linear-gradient(135deg, #2d2e48 0%, #1e1e2f 100%)',
                  borderBottom: '1px solid rgba(104, 113, 209, 0.2)',
                }}
              >
                <TableCell
                  sx={{
                    color: '#6871d1',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    borderTopLeftRadius: 12,
                  }}
                >
                  Username
                </TableCell>
                <TableCell
                  sx={{
                    color: '#6871d1',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                  }}
                >
                  Priority
                </TableCell>
                <TableCell
                  sx={{
                    color: '#6871d1',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                  }}
                >
                  Status
                </TableCell>
                <TableCell
                  sx={{
                    color: '#6871d1',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                  }}
                >
                  Tweets Processed
                </TableCell>
                <TableCell
                  sx={{
                    color: '#6871d1',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                  }}
                >
                  Replies
                </TableCell>
                <TableCell
                  sx={{
                    color: '#6871d1',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                  }}
                >
                  Success Rate
                </TableCell>
                <TableCell
                  sx={{
                    color: '#6871d1',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                  }}
                >
                  Last Processed
                </TableCell>
                <TableCell
                  sx={{
                    color: '#6871d1',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    borderTopRightRadius: 12,
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {handles.map((handle, index) => (
                <TableRow
                  key={handle._id}
                  sx={{
                    background: index % 2 === 0 ? '#23234a' : '#1e1e2f',
                    transition: 'background 0.2s',
                    '&:hover': {
                      background: 'rgba(104, 113, 209, 0.1)',
                    },
                  }}
                >
                  <TableCell
                    sx={{
                      color: '#fff',
                      fontWeight: 600,
                      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TwitterIcon sx={{ color: '#6871d1', fontSize: 20 }} />@{handle.username}
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{ color: '#fff', borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}
                  >
                    <Chip
                      label={`Priority ${handle.priority}`}
                      size='small'
                      sx={{
                        background:
                          handle.priority === 1
                            ? 'rgba(244, 67, 54, 0.2)'
                            : 'rgba(255, 152, 0, 0.2)',
                        color: handle.priority === 1 ? '#F44336' : '#FF9800',
                        border: `1px solid ${handle.priority === 1 ? '#F44336' : '#FF9800'}`,
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        borderRadius: 2,
                        transition: 'all 0.2s',
                        '&:hover': {
                          transform: 'scale(1.05)',
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{ color: '#fff', borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}
                  >
                    <Chip
                      label={handle.isActive ? 'Active' : 'Inactive'}
                      size='small'
                      sx={{
                        background: handle.isActive
                          ? 'rgba(76, 175, 80, 0.2)'
                          : 'rgba(158, 158, 158, 0.2)',
                        color: handle.isActive ? '#4CAF50' : '#9E9E9E',
                        border: `1px solid ${handle.isActive ? '#4CAF50' : '#9E9E9E'}`,
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        borderRadius: 2,
                        transition: 'all 0.2s',
                        '&:hover': {
                          transform: 'scale(1.05)',
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      color: '#fff',
                      fontWeight: 600,
                      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                    }}
                  >
                    {handle.totalTweetsProcessed}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: '#fff',
                      fontWeight: 600,
                      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                    }}
                  >
                    {handle.totalReplies}
                  </TableCell>
                  <TableCell
                    sx={{ color: '#fff', borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}
                  >
                    <Chip
                      label={`${handle.successRate.toFixed(2)}%`}
                      size='small'
                      sx={{
                        background:
                          handle.successRate > 80
                            ? 'rgba(76, 175, 80, 0.2)'
                            : handle.successRate > 50
                              ? 'rgba(255, 152, 0, 0.2)'
                              : 'rgba(244, 67, 54, 0.2)',
                        color:
                          handle.successRate > 80
                            ? '#4CAF50'
                            : handle.successRate > 50
                              ? '#FF9800'
                              : '#F44336',
                        border: `1px solid ${handle.successRate > 80 ? '#4CAF50' : handle.successRate > 50 ? '#FF9800' : '#F44336'}`,
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        borderRadius: 2,
                        transition: 'all 0.2s',
                        '&:hover': {
                          transform: 'scale(1.05)',
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      color: '#fff',
                      fontWeight: 600,
                      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                    }}
                  >
                    {formatDate(handle.lastProcessed)}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        size='small'
                        onClick={() => handleEdit(handle)}
                        sx={{
                          backgroundColor: '#6871d1',
                          color: '#fff',
                          borderRadius: '12px',
                          boxShadow: '0 4px 12px rgba(104, 113, 209, 0.4)',
                          fontWeight: 500,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            backgroundColor: '#5a63bb',
                            boxShadow: '0 6px 16px rgba(104, 113, 209, 0.6)',
                            color: '#fff',
                          },
                          '&:disabled': {
                            backgroundColor: '#aeb3e9',
                            color: '#fff',
                            opacity: 0.7,
                          },
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size='small'
                        onClick={() => handleDelete(handle._id)}
                        sx={{
                          backgroundColor: '#6871d1',
                          color: '#fff',
                          borderRadius: '12px',
                          boxShadow: '0 4px 12px rgba(104, 113, 209, 0.4)',
                          fontWeight: 500,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            backgroundColor: '#F44336',
                            boxShadow: '0 6px 16px rgba(244, 67, 54, 0.3)',
                            color: '#fff',
                          },
                          '&:disabled': {
                            backgroundColor: '#aeb3e9',
                            color: '#fff',
                            opacity: 0.7,
                          },
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth='sm' fullWidth>
        <DialogTitle sx={{ color: '#fff', background: '#2b2c3e' }}>
          {editingHandle ? 'Edit Twitter Handle' : 'Add Twitter Handle'}
        </DialogTitle>
        <DialogContent sx={{ background: '#2b2c3e', pt: 2 }}>
          <TextField
            fullWidth
            label='Username'
            value={formData.username}
            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
            sx={{ mb: 2, background: '#23234a', borderRadius: 2, input: { color: '#fff' } }}
            InputProps={{
              sx: { color: '#fff' },
            }}
            InputLabelProps={{
              sx: { color: '#aaa' },
            }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel sx={{ color: '#aaa' }}>Priority</InputLabel>
            <Select
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value as number })}
              sx={{ color: '#fff', background: '#23234a', borderRadius: 2 }}
              MenuProps={{ PaperProps: { sx: { background: '#23234a', color: '#fff' } } }}
            >
              <MenuItem value={1}>1</MenuItem>
              <MenuItem value={2}>2</MenuItem>
              <MenuItem value={3}>3</MenuItem>
              <MenuItem value={4}>4</MenuItem>
              <MenuItem value={5}>5</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#6871d1',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#6871d1',
                  },
                }}
              />
            }
            label='Active'
            sx={{ color: '#fff' }}
          />
        </DialogContent>
        <DialogActions sx={{ background: '#2b2c3e', p: 2 }}>
          <Button
            onClick={handleCloseDialog}
            sx={{
              backgroundColor: 'transparent',
              color: '#6871d1',
              border: '1.5px solid #6871d1',
              py: 1.5,
              borderRadius: '12px',
              fontWeight: 500,
              transition: 'all 0.3s ease',
              px: 3,
              fontSize: '1rem',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: 'rgba(104,113,209,0.08)',
                color: '#fff',
                border: '1.5px solid #6871d1',
              },
              '&:disabled': {
                backgroundColor: '#e0e3f7',
                color: '#aaa',
                opacity: 0.7,
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant='contained'
            sx={{
              backgroundColor: '#6871d1',
              color: '#fff',
              py: 1.5,
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(104, 113, 209, 0.4)',
              fontWeight: 500,
              transition: 'all 0.3s ease',
              px: 3,
              fontSize: '1rem',
              '&:hover': {
                backgroundColor: '#5a63bb',
                boxShadow: '0 6px 16px rgba(104, 113, 209, 0.6)',
              },
              '&:disabled': {
                backgroundColor: '#aeb3e9',
                color: '#fff',
                opacity: 0.7,
              },
            }}
          >
            {editingHandle ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          sx={{
            width: '100%',
            background: '#2b2c3e',
            color: '#fff',
            border: '1px solid rgba(104,113,209,0.15)',
          }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TwitterDashboard;
